"use client";

import { useEffect, useState } from "react";

interface NavigationProps {
  menu: {
    id: string;
    number: string;
    title: string;
    subMenu: { id: string; number: string; title: string }[];
  }[];
}

export default function Navigation({ menu }: NavigationProps) {
  const [activeHash, setActiveHash] = useState("");

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    const id = hash.replace("#", "");
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      // Update the URL hash after scrolling
      history.pushState(null, "", hash);
      setActiveHash(hash);
    }
  };

  useEffect(() => {
    const handleHashChange = () => {
      setActiveHash(window.location.hash);
    };

    // Set initial hash
    setActiveHash(window.location.hash);

    // Set up intersection observer
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActiveHash(`#${id}`);
            // Update URL hash without scrolling
            history.replaceState(null, "", `#${id}`);
          }
        });
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0.2,
      }
    );

    // Observe all sections
    menu.forEach(item => {
      const element = document.getElementById(`section-${item.number}`);
      if (element) observer.observe(element);

      item.subMenu.forEach(subItem => {
        const subElement = document.getElementById(
          `section-${item.number}-${subItem.number.split(".")[1]}`
        );
        if (subElement) observer.observe(subElement);
      });
    });

    // Listen for hash changes from clicking links
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [menu]);

  return (
    <div className="h-screen sticky top-[170px] mb-[124px]">
      <nav className="h-full overflow-y-auto rounded-lg bg-white p-4">
        <ul className="space-y-4">
          {menu.map(item => (
            <li key={item.id} className="capitalize">
              <a
                href={`#section-${item.number}`}
                onClick={e => scrollToSection(e, `#section-${item.number}`)}
                className={`block w-full text-left text-sm md:text-[15px]  transition-colors font-medium wireless-Medium ${
                  activeHash === `#section-${item.number}`
                    ? "text-system-primary"
                    : "text-midnightIndigo-400 hover:text-system-primary"
                }`}
              >
                {item.number}. {item.title}
              </a>
              {item.subMenu.length > 0 && (
                <ul className="ml-4 mt-2 space-y-4 mb-8">
                  {item.subMenu.map(subItem => (
                    <li key={subItem.id}>
                      <a
                        href={`#section-${item.number}-${subItem.number.split(".")[1]}`}
                        onClick={e =>
                          scrollToSection(
                            e,
                            `#section-${item.number}-${subItem.number.split(".")[1]}`
                          )
                        }
                        className={`block text-sm transition-colors ${
                          activeHash === `#section-${item.number}-${subItem.number.split(".")[1]}`
                            ? "text-system-primary"
                            : "text-midnightIndigo-400 hover:hover:text-system-primary"
                        }`}
                      >
                        {subItem.number} {subItem.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
